<template>
  <el-dialog :title="label('title')" :visible.sync="visible" width="90%" top="5vh" append-to-body @close="invalidate">
    <div v-loading="loading" class="postage-review" :aria-busy="loading">
      <div class="postage-review__context">
        <span>{{ label('invoice') }}: <strong>{{ context.invoiceNumber }}</strong></span>
        <span>{{ label('carrier') }}: <strong>{{ context.logisticsCompany }}</strong></span>
        <div class="postage-review__file">{{ label('file') }}: {{ context.fileName }}</div>
      </div>
      <el-alert v-if="error" :title="label('error')" type="error" :closable="false" show-icon />
      <template v-if="result">
        <div class="postage-review__totals">
          <div class="postage-review__card">
            <span>{{ label('current') }}</span>
            <strong>{{ money(result.currentChargeAmount) }}</strong>
          </div>
          <div class="postage-review__card">
            <span>{{ label('total') }}</span>
            <strong>{{ money(result.customerChargeTotal) }}</strong>
          </div>
        </div>
        <el-table :data="result.rows" border stripe max-height="460" show-summary :summary-method="summary" :empty-text="label('empty')">
          <el-table-column type="index" :label="label('no')" width="65" />
          <el-table-column prop="customerInvoiceId" :label="label('customerInvoice')" min-width="190">
            <template slot-scope="scope">{{ scope.row.customerInvoiceId || label('notInvoiced') }}</template>
          </el-table-column>
          <el-table-column prop="clientCode" :label="label('client')" min-width="130" />
          <el-table-column prop="chargedPrice" :label="label('charged')" min-width="160" align="right">
            <template slot-scope="scope">{{ money(scope.row.chargedPrice) }}</template>
          </el-table-column>
        </el-table>
      </template>
    </div>
    <span slot="footer">
      <el-button :disabled="loading" icon="el-icon-refresh" @click="load">{{ label(error ? 'retry' : 'refresh') }}</el-button>
      <el-button type="primary" :disabled="loading || !result || error" icon="el-icon-download" @click="exportCsv">{{ label('export') }}</el-button>
      <el-button @click="visible = false">{{ label('close') }}</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { billApi } from '@/api';
import { downloadBlob } from '@/utils/index';
import { reviewAmount, reviewMoney, reviewCsv, reviewFilename } from '../invoice-review';

export default {
  name: 'PostageInvoiceReviewDialog',
  data() {
    return { visible: false, loading: false, error: false, result: null, context: {}, sourceRow: null, requestVersion: 0 };
  },
  beforeDestroy() {
    this.invalidate();
  },
  methods: {
    label(key) {
      return this.$t(`billingPage.postageReview.${key}`);
    },
    money: reviewMoney,
    show(row) {
      this.sourceRow = row;
      this.context = { ...row };
      this.visible = true;
      this.load();
    },
    invalidate() {
      this.requestVersion += 1;
      this.result = null;
      this.loading = false;
    },
    async load() {
      const version = ++this.requestVersion;
      const row = this.sourceRow;
      this.loading = true;
      this.error = false;
      this.result = null;
      try {
        const response = await billApi.getPostageRechargeInvoiceReview({ id: String(row.id) });
        if (version !== this.requestVersion || !this.visible) return;
        const data = response.data;
        if (response.code != 200 || !data || String(data.id) !== String(row.id) || !Array.isArray(data.rows)) {
          throw new Error('Invalid invoice review response');
        }
        reviewAmount(data.currentChargeAmount);
        reviewAmount(data.customerChargeTotal);
        data.rows.forEach(item => reviewAmount(item.chargedPrice));
        this.result = data;
        this.context = data;
        this.$emit('loaded', { row, currentChargeAmount: data.currentChargeAmount });
      } catch (error) {
        if (version === this.requestVersion && this.visible) this.error = true;
      } finally {
        if (version === this.requestVersion) this.loading = false;
      }
    },
    summary() {
      return [this.label('sum'), '', '', this.money(this.result.customerChargeTotal)];
    },
    exportCsv() {
      if (this.loading || this.error || !this.result) return;
      downloadBlob(new Blob([reviewCsv(this.result)], { type: 'text/csv;charset=utf-8;' }), reviewFilename(this.result.invoiceNumber));
    }
  }
};
</script>

<style lang="scss" scoped>
.postage-review {
  min-height: 180px;
  &__context { line-height: 1.8; margin-bottom: 20px; span { margin-right: 32px; } }
  &__file { overflow-wrap: anywhere; }
  &__totals { display: flex; align-items: center; gap: 20px; margin-bottom: 24px; flex-wrap: wrap; }
  &__card {
    flex: 1; min-width: 220px; padding: 20px; border: 1px solid #e5e7ef; border-top: 3px solid #5044a5; border-radius: 6px;
    span { display: block; color: #606266; margin-bottom: 12px; }
    strong { font-size: 26px; color: #303133; font-variant-numeric: tabular-nums; }
  }
}
</style>
